import * as electron from "electron";
import { windowsIndex } from "./mainStore";

interface ToolbarLocation {
	bottom: number;
	height: number;
	left: number;
	right: number;
	top: number;
	width: number;
	x: number;
	y: number;
}

interface TaskbarLocation {
	top: number;
	left: number;
	right: number;
	bottom: number;
	width: number;
	height: number;
	edge: string;
}

const defaultToolbarLocation = {
	bottom: 0,
	height: 0,
	left: 0,
	right: 0,
	top: 0,
	width: 0,
	x: 0,
	y: 0,
};

const defaultTaskbarLocation = {
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	width: 0,
	height: 0,
	edge: "",
};

function buildUnscaledDisplays() {
	const displays = electron.screen.getAllDisplays() as any[];
	let primaryDisplay = electron.screen.getPrimaryDisplay() as any;

	// compute right and bottom
	for (let i = 0; i < displays.length; i++) {
		const display = displays[i];
		display.bounds.right = display.bounds.x + display.bounds.width;
		display.bounds.bottom = display.bounds.y + display.bounds.height;
		if (display.id == primaryDisplay.id) primaryDisplay = display as Electron.Display & { unscaledBounds: any };
	}

	if (primaryDisplay.scaleFactor != 1) {
		// assume primary is always at 0,0
		primaryDisplay.unscaledBounds = {
			x: primaryDisplay.bounds.x,
			y: primaryDisplay.bounds.y,
			height: primaryDisplay.bounds.height * primaryDisplay.scaleFactor,
			width: primaryDisplay.bounds.width * primaryDisplay.scaleFactor,
		};
		primaryDisplay.unscaledBounds.right = primaryDisplay.unscaledBounds.x + primaryDisplay.unscaledBounds.width;
		primaryDisplay.unscaledBounds.bottom = primaryDisplay.unscaledBounds.y + primaryDisplay.unscaledBounds.height;
	}

	// get unscaled positioning X
	let thisDisplay = primaryDisplay;
	while (thisDisplay.nextX) {
		const previousDisplay = thisDisplay;
		thisDisplay = thisDisplay.nextX;
		thisDisplay.unscaledBounds = {
			x: previousDisplay.unscaledBounds.right,
			width: thisDisplay.bounds.width * thisDisplay.scaleFactor,
		};
		thisDisplay.unscaledBounds.right = thisDisplay.unscaledBounds.x + thisDisplay.unscaledBounds.width;
	}

	thisDisplay = primaryDisplay;
	while (thisDisplay.previousX) {
		const previousDisplay = thisDisplay;
		thisDisplay = thisDisplay.previousX;
		thisDisplay.unscaledBounds = {
			right: previousDisplay.unscaledBounds.x,
			width: thisDisplay.bounds.width * thisDisplay.scaleFactor,
		};
		thisDisplay.unscaledBounds.x = thisDisplay.unscaledBounds.right - thisDisplay.unscaledBounds.width;
	}

	// get unscaled positioning Y
	thisDisplay = primaryDisplay;
	while (thisDisplay.nextY) {
		const previousDisplay = thisDisplay;
		thisDisplay = thisDisplay.nextY;
		thisDisplay.unscaledBounds.y = previousDisplay.unscaledBounds.top;
		thisDisplay.unscaledBounds.height = thisDisplay.bounds.height * thisDisplay.scaleFactor;
		thisDisplay.unscaledBounds.bottom = thisDisplay.unscaledBounds.y + thisDisplay.unscaledBounds.height;
	}

	thisDisplay = primaryDisplay;
	while (thisDisplay.previousY) {
		const previousDisplay = thisDisplay;
		thisDisplay = thisDisplay.previousY;
		thisDisplay.unscaledBounds.bottom = previousDisplay.unscaledBounds.y;
		thisDisplay.unscaledBounds.height = thisDisplay.bounds.height * thisDisplay.scaleFactor;
		thisDisplay.unscaledBounds.y = thisDisplay.unscaledBounds.bottom - thisDisplay.unscaledBounds.height;
	}

	return displays;
}

export default {
	getMonitorInfo(cb: Function) {
		const displays = buildUnscaledDisplays();
		const pDisplay = electron.screen.getPrimaryDisplay();
		const mi: any = {
			primaryMonitor: {},
			nonPrimaryMonitors: [],
			virtualScreen: {
				width: pDisplay.bounds.width,
				height: pDisplay.bounds.height,
				bottom: 0,
				top: 0,
				right: 0,
				left: 0,
			},
		};
		let count = 0;

		// Retrieves the toolbar location using windowsIndex
		const getToolbarLocation = (): ToolbarLocation => {
			let toolbar: any;
			const found = Object.keys(windowsIndex.byWindowName).find((key) => key.startsWith("Toolbar-"));
			toolbar = found ? windowsIndex.byWindowName[found] : {};
			return toolbar && toolbar.location ? toolbar.location : defaultToolbarLocation;
		};

		displays.forEach((display) => {
			// console.log("electron.screen",display.workArea)
			// let availableScaledRect = electron.screen.dipToScreenRect(window.window,display.workArea);
			// console.log("availableScaledRect",availableScaledRect)
			let taskbarDefault: TaskbarLocation | {} = defaultTaskbarLocation;
			const toolbarLoc = getToolbarLocation();
			const monitor = {
				taskbar: taskbarDefault,
				monitor: {
					dipRect: {
						width: display.workAreaSize.width,
						height: display.workAreaSize.height,
						bottom: display.workArea.height + display.workArea.y, // bottom-most monitor coordinate
						left: display.workArea.x, // left-most monitor coordinate
						right: display.workArea.x + display.workArea.width, // right-most monitor coordinate
						top: display.workArea.y, // top-most monitor coordinate
					},
					scaledRect: {
						width: display.workAreaSize.width,
						height: display.workAreaSize.height,
						bottom: display.workArea.height + display.workArea.y, // bottom-most monitor coordinate
						left: display.workArea.x, // left-most monitor coordinate
						right: display.workArea.x + display.workArea.width, // right-most monitor coordinate
						top: display.workArea.y, // top-most monitor coordinate
					},
				},
				deviceScaleFactor: display.scaleFactor,
				availableRect: {
					width: display.workAreaSize.width,
					height: display.workAreaSize.height,
					bottom: display.workArea.height + display.workArea.y, // bottom-most monitor coordinate
					left: display.workArea.x, // left-most monitor coordinate
					right: display.workArea.x + display.workArea.width, // right-most monitor coordinate
					top: display.workArea.y,
				},
				name: `device${count++}`,
				deviceId: display.id.toString(), // device id of the display,
				displayDeviceActive: true, // true if the display is active
				monitorRect: {
					width: display.size.width,
					height: display.size.height,
					bottom: display.bounds.height + display.bounds.y, // bottom-most monitor coordinate
					left: display.bounds.x, // left-most monitor coordinate
					right: display.bounds.x + display.bounds.width, // right-most monitor coordinate
					top: display.bounds.y, // top-most monitor coordinate
				},
			};

			// taskbar location
			let taskbar;
			// if the monitors right most pixel does not match the right most available pixel we can use then the taskbar is occupying the unavailable space
			if (monitor.monitorRect.right != monitor.availableRect.right) {
				taskbar = {
					top: monitor.monitorRect.top,
					left: monitor.availableRect.right,
					right: monitor.monitorRect.right,
					bottom: monitor.monitorRect.bottom,
					width: monitor.monitorRect.width - monitor.availableRect.width,
					height: monitor.monitorRect.height,
					edge: "right",
				};
				// if the monitors left most pixel does not match the left most available pixel we can use then the taskbar is occupying the unavailable space
			} else if (monitor.monitorRect.left != monitor.availableRect.left) {
				taskbar = {
					top: monitor.monitorRect.top,
					left: monitor.monitorRect.left,
					right: monitor.availableRect.left,
					bottom: monitor.monitorRect.bottom,
					width: monitor.monitorRect.width - monitor.availableRect.width,
					height: monitor.monitorRect.height,
					edge: "left",
				};
				// The calculations bellow get complicated for the top and bottom due to claimed space on the y-plane. Aavailable rect only cares if that space is usable or not.
				// if the monitors bottom most pixel does not match the bottom most available pixel we can use AND monitors top most pixel and top most available pixel matches
				// then the taskbar and POTENTIALLY the claimed space are occupying the unavailable space.
			} else if (
				monitor.monitorRect.bottom != monitor.availableRect.bottom &&
				monitor.monitorRect.top == monitor.availableRect.top
			) {
				taskbar = {
					top: monitor.availableRect.bottom,
					left: monitor.availableRect.left,
					right: monitor.availableRect.right,
					bottom: monitor.monitorRect.bottom,
					width: monitor.availableRect.width,
					height: monitor.monitorRect.bottom - monitor.availableRect.bottom,
					edge: "bottom",
				};
				// if the bottom most pixel of the available rect matches the top of the toolbar location, then we can assume that the claimed space occupies a portion of the bottom section
				if (monitor.availableRect.bottom === toolbarLoc.top) {
					taskbar.height = taskbar.height - toolbarLoc.height;
					taskbar.top = taskbar.top + toolbarLoc.height;
				}
			} else if (
				monitor.monitorRect.top != monitor.availableRect.top &&
				monitor.monitorRect.bottom == monitor.availableRect.bottom
			) {
				taskbar = {
					top: monitor.monitorRect.top,
					left: monitor.availableRect.left,
					right: monitor.availableRect.right,
					bottom: monitor.availableRect.top,
					width: monitor.availableRect.width,
					height: monitor.availableRect.top - monitor.monitorRect.top,
					edge: "top",
				};
				// if the top most pixel of the available rect matches the bottom of the toolbar location, then we can assume that the claimed space occupies a portion of the top section
				if (monitor.availableRect.top === toolbarLoc.bottom) {
					taskbar.height = taskbar.height - toolbarLoc.height;
					taskbar.bottom = taskbar.bottom - toolbarLoc.height;
				}
				// this covers the situtation when claimed space and the taskbar occupy both the top and bottom of the monitor. e.g the toolbar is docked to the top of the monitor and the taskbar is at the bottom
			} else {
				taskbar = {
					top: 0,
					left: monitor.availableRect.left,
					right: monitor.availableRect.right,
					bottom: 0,
					width: monitor.availableRect.width,
					height: 0,
					edge: "",
				};
				if (monitor.availableRect.bottom === toolbarLoc.top) {
					taskbar.edge = "top";
					taskbar.height = monitor.availableRect.top - monitor.monitorRect.top;
					taskbar.bottom = monitor.availableRect.top;
					taskbar.top = monitor.monitorRect.top;
				} else {
					taskbar.edge = "bottom";
					taskbar.height = monitor.monitorRect.bottom - monitor.availableRect.bottom;
					taskbar.bottom = monitor.monitorRect.bottom;
					taskbar.top = monitor.availableRect.bottom;
				}
			}

			monitor.taskbar = taskbar.height === 0 ? {} : taskbar;

			if (pDisplay.id === display.id) {
				mi.primaryMonitor = monitor;
				return;
			}
			mi.nonPrimaryMonitors.push(monitor);
		});
		return cb(mi);
	},
};
